import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Code, Copy, ChevronRight, Filter, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { staggerContainer, staggerItem, hoverLift } from "@/config/animations";
import { PlatformIcon } from "@/components/logos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  crewai: "#10b981", autogen: "#3b82f6", langgraph: "#f59e0b",
  manus: "#8b5cf6", mcp: "#e87b35", javascript: "#f7df1e",
  python: "#3776ab", curl: "#06b6d4",
};

const DIFFICULTY_COLORS = {
  beginner: "var(--qn-success)", intermediate: "var(--qn-warning)", advanced: "var(--qn-error)",
};

export default function CodeExamplesTab() {
  const [examples, setExamples] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/examples`).then(r => setExamples(r.data)).catch(() => {});
  }, []);

  const loadExample = async (id) => {
    try {
      const r = await axios.get(`${API}/examples/${id}`);
      setDetail(r.data);
      setSelected(id);
    } catch { toast.error("Failed to load example"); }
  };

  const copyCode = () => {
    if (detail?.code) {
      navigator.clipboard.writeText(detail.code);
      toast.success("Code copied!");
    }
  };

  const platforms = [...new Set(examples.map(e => e.platform))];
  const filtered = examples.filter(e => {
    if (filterPlatform && e.platform !== filterPlatform) return false;
    if (search && !e.title.toLowerCase().includes(search.toLowerCase()) && !e.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="code-examples-tab" data-automation-id="code-examples-tab">
      <div>
        <h2 className="text-lg font-semibold text-[var(--qn-foreground)]">Code Examples</h2>
        <p className="text-sm text-[var(--qn-foreground-muted)]">{examples.length} copy-paste ready examples for every platform</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-subtle)]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search examples..." className="pl-10 bg-[var(--qn-surface)]" data-testid="examples-search" data-automation-id="examples-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button variant={!filterPlatform ? "default" : "outline"} size="sm" onClick={() => setFilterPlatform(null)} className={!filterPlatform ? "btn-primary" : ""}>All</Button>
          {platforms.map(p => (
            <Button key={p} variant={filterPlatform === p ? "default" : "outline"} size="sm" onClick={() => setFilterPlatform(p)} className={filterPlatform === p ? "btn-primary" : ""} data-testid={`filter-${p}`}>
              <PlatformIcon platformId={p} size="sm" className="mr-1.5 w-4 h-4" />
              {p}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-2">
            {filtered.map(ex => (
              <motion.div key={ex.id} variants={staggerItem} whileHover={hoverLift.whileHover}>
                <Card
                  className={`card-themed cursor-pointer transition-all ${selected === ex.id ? "ring-2 ring-[var(--qn-primary)]" : ""}`}
                  onClick={() => loadExample(ex.id)}
                  data-testid={`example-card-${ex.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2">
                      <Code className="w-4 h-4 mt-0.5 shrink-0" style={{ color: PLATFORM_COLORS[ex.platform] }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{ex.title}</div>
                        <p className="text-xs text-[var(--qn-foreground-muted)] line-clamp-1 mt-0.5">{ex.description}</p>
                        <div className="flex gap-1.5 mt-1.5">
                          <Badge variant="outline" className="text-[10px]" style={{ borderColor: PLATFORM_COLORS[ex.platform] + "60", color: PLATFORM_COLORS[ex.platform] }}>{ex.platform}</Badge>
                          <Badge variant="outline" className="text-[10px]" style={{ color: DIFFICULTY_COLORS[ex.difficulty] }}>{ex.difficulty}</Badge>
                          <Badge variant="outline" className="text-[10px]">{ex.language}</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            {filtered.length === 0 && <div className="text-center py-8 text-sm text-[var(--qn-foreground-muted)]">No examples match your filter</div>}
          </motion.div>
        </div>

        {/* Code Viewer */}
        <div className="lg:col-span-2">
          {!detail ? (
            <Card className="card-themed h-full">
              <CardContent className="p-12 text-center text-[var(--qn-foreground-muted)] flex flex-col items-center justify-center h-full min-h-[400px]">
                <Code className="w-12 h-12 mb-4 opacity-30" />
                <p>Select an example to view code</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-themed">
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div>
                  <CardTitle className="text-base">{detail.title}</CardTitle>
                  <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">{detail.description}</p>
                </div>
                <Button variant="outline" size="sm" onClick={copyCode} data-testid="copy-example-btn" data-automation-id="copy-example-btn">
                  <Copy className="w-3 h-3 mr-1" />Copy
                </Button>
              </CardHeader>
              <CardContent>
                <pre className="text-xs font-mono bg-[var(--qn-background)] p-4 rounded-lg overflow-x-auto text-[var(--qn-foreground-muted)] border border-[var(--qn-border)]" style={{ maxHeight: "60vh" }}>
                  {detail.code}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
